import { Box, Button, FormLabel, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { getCoordinatesByAddress } from '../../api/nominatium';
import { FormInputPhone } from '../../form-components/FormInputPhone';
import { FormInputRadio } from '../../form-components/FormInputRadio';
import { FormInputText } from '../../form-components/FormInputText';
import { FormInputBRN } from '../../form-components/FormInputBRN';
import { errorMessages } from '../../utils/methods';
import { createCenters } from '../../api/store-management';
import { useSelector } from 'react-redux';
import { useApp } from '../../context/AppContext';

const options = [
  {
    label: 'Individual',
    value: 'INDIVIDUAL',
  },
  {
    label: 'Entity',
    value: 'CORPORATION',
  },
];

const StoreCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const token = useSelector(state => state.user.user.token);
  const user = useSelector(state => state.user.user);
  const { centersId } = useApp();

  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');

  const scriptUrl = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  const open = useDaumPostcodePopup(scriptUrl);

  const { handleSubmit, control, setValue, setError } = useForm();

  const handleComplete = async data => {
    setValue('city', data.sigungu);
    setValue('jibunAddress', data.jibunAddress);
    setValue('postalCode', data.zonecode);

    await getCoordinatesByAddress(data.addressEnglish).then(data => {
      setValue('latitude', data[0].coordinate.latitude);
      setValue('longitude', data[0].coordinate.longitude);
      // setValue('region', data.sigungu);
    });
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  const submit = async values => {
    if (values.password !== values.verifyPassword) {
      return setError('verifyPassword', {
        type: 'custom',
        message: 'Password not matched',
      });
    }

    const body = {
      managerInfo: {
        username: values.username,
        name: values.name,
        password: values.password,
        phoneNumber: values.phoneNumber,
        email: values.email,
      },
      centerInfo: {
        businessName: values.businessName,
        businessNumber: values.businessNumber,
        businessClassification: values.businessClassification,
        businessType: values.businessType,
        postalCode: values.postalCode,
        city: values.city,
        address: values.jibunAddress,
        latitude: values.latitude,
        longitude: values.longitude,
        addressDetail: values.addressDetail,
        representativeNumber: values.representativeNumber,
        email: values.email_1,
        homepage: values.homepage,
      },
      headCenterId: user?.headBranch?.headBranchId,
    };

    try {
      await createCenters(token, body);
      toast.success(t('Successfully updated'));
      navigate('/store');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    setValue('businessClassification', 'INDIVIDUAL');
  }, []);

  return (
    <Box sx={{ background: 'white', borderRadius: '5px', width: '1080px', px: 2.6, py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Link to="/store" style={{ textDecoration: 'none', color: '#3a3a3a', display: 'flex' }}>
          <img src="/images/backIcon.svg" alt="backIcon" />
          <Typography sx={{ fontSize: '18px', lineHeight: '24px', ml: '8px', fontWeight: 700 }}>지점 등록</Typography>
        </Link>

        <Link to="/store" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <img src="/images/icons/align-justify.svg" alt="align-justify" width={18} height={18} />
          <Typography sx={{ ml: 0.75, color: '#6B7280', fontSize: '14px', fontWeight: 500, lineHeight: '16px' }}>
            목록보기
          </Typography>
        </Link>
      </Box>
      <hr />

      <Box sx={{ display: 'flex', alignItems: 'center', mt: '30px' }}>
        <Typography sx={{ width: '30%', fontSize: '16px', fontWeight: 500, mr: '10px' }}>
          {t('General Information')}
        </Typography>
      </Box>
      <Box style={{ display: 'flex', columnGap: '50px' }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box className="form1">
              <Typography className="header1">{t('Business Name*')}</Typography>
              <FormInputText
                color="info"
                control={control}
                sx={{ width: '405px' }}
                rules={{ required: true }}
                size="small"
                required
                fullWidth
                variant="outlined"
                name="businessName"
                placeholder={t('Branch Name')}
              />
              <Typography className="header1">{t('Employer Identification Number*')}</Typography>
              <FormInputBRN
                color="info"
                control={control}
                sx={{ width: '405px' }}
                rules={{ required: true }}
                size="small"
                fullWidth
                variant="outlined"
                name="businessNumber"
                placeholder={t('Enter the business identification number')}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '40px',
                mt: '25px',
              }}>
              <FormLabel name="controlled-radio-buttons-group" sx={{ color: '#0f7f85', fontWeight: 500 }}>
                {t('Employer Type*')}
              </FormLabel>
              <FormInputRadio
                name="businessClassification"
                rules={{ required: true }}
                control={control}
                options={options}
                row
              />
            </Box>
            <Box className="form1">
              <Typography className="header1">{t('Type of Business')}</Typography>
              <FormInputText
                color="info"
                control={control}
                sx={{ width: '405px' }}
                size="small"
                fullWidth
                variant="outlined"
                name="businessType"
                placeholder={t('Enter the type of business')}
              />

              <Typography className="header1">{t('Phone Number*')}</Typography>
              <FormInputPhone
                control={control}
                sx={{ width: '405px' }}
                setValue={setValue}
                rules={{ required: true }}
                name="representativeNumber"
                placeholder={t('Enter the phone number')}
              />

              <Typography className="header1">{t('E-mail')}</Typography>
              <FormInputText
                color="info"
                control={control}
                sx={{ width: '405px' }}
                size="small"
                fullWidth
                type="email"
                variant="outlined"
                name="email_1"
                placeholder={t('Enter the E-mail address')}
              />
              <Typography className="header1">{t('Mailing Address*')}</Typography>
              {/* <TextField
                onMouseOver={e => (e.target.style.cursor = 'pointer')}
                sx={{
                  width: '405px',
                  mb: '20px',
                  '.MuiInputBase-formControl': {
                    fontWeight: 'bold',
                    fontSize: '16px',
                    padding: '8.5px 14px',
                  },
                }}
                {...register('jibunAddress', { required: true })}
                value={address}
                multiline
                error={errors.jibunAddress}
                name="jibunAddress"
                placeholder={t('Mailing Address*')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      sx={{ borderColor: errors.jibunAddress ? 'red' : '' }}
                      position="end"
                      onClick={handleClick}>
                      <MySearchIcon />
                    </InputAdornment>
                  ),
                }}
              /> */}
              <Stack sx={{ flexDirection: 'row', mb: 2.25 }}>
                <FormInputText
                  control={control}
                  name="jibunAddress"
                  sx={{ width: '334px' }}
                  size="small"
                  placeholder={t('Mailing Address*')}
                />
                <Button
                  onClick={handleClick}
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    ml: 1,
                    '&:hover': { color: '#fff' },
                  }}>
                  검색
                </Button>
              </Stack>

              <FormInputText
                color="info"
                control={control}
                sx={{ width: '405px' }}
                size="small"
                fullWidth
                variant="outlined"
                name="addressDetail"
                placeholder={t('Address details')}
              />
              <Typography className="header1">{t('Web site')}</Typography>
              <FormInputText
                color="info"
                control={control}
                sx={{
                  width: '405px',
                  mb: '20px',
                  '.css-1x51dt5-MuiInputBase-input-MuiInput-input': {
                    fontSize: '16px',
                  },
                }}
                size="small"
                fullWidth
                variant="outlined"
                name="homepage"
                placeholder={t('Home page URL')}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className="form1" sx={{ mt: '21px' }}>
              <Typography className="header1">{t('Name*')}</Typography>
              <FormInputText
                color="info"
                control={control}
                size="small"
                fullWidth
                rules={{ required: true }}
                variant="outlined"
                name="name"
                placeholder={t('Enter executor name')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('ID*')}</Typography>
              <FormInputText
                color="info"
                control={control}
                size="small"
                fullWidth
                rules={{ required: true }}
                variant="outlined"
                name="username"
                placeholder={t('Enter your ID')}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Password')}</Typography>
              <FormInputText
                color="info"
                control={control}
                type="password"
                size="small"
                fullWidth
                variant="outlined"
                name="password"
                placeholder={t(
                  'Passwords must contain at least two of the following: uppercase letters, lowercase letters, numbers, and symbols',
                )}
                sx={{ width: '405px' }}
              />
              <Typography className="header1">{t('Confirm Password')}</Typography>
              <FormInputText
                type="password"
                control={control}
                name="verifyPassword"
                placeholder={t('Must match the previous entry')}
                sx={{ width: '405px' }}
              />

              <Typography className="header1">{t('Phone Number')}</Typography>
              <FormInputPhone
                control={control}
                setValue={setValue}
                name="phoneNumber"
                placeholder={t('Enter your phone number')}
                sx={{ width: '405px' }}
              />

              <Typography className="header1">{t('E-mail')}</Typography>
              <FormInputText
                color="info"
                control={control}
                size="small"
                fullWidth
                type="email"
                variant="outlined"
                name="email"
                placeholder={t('Enter your E-mail address')}
                sx={{ width: '405px' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <hr />
      <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
        <Button
          // onClick={() => reset()}
          href="/store"
          variant="contained"
          sx={{
            borderRadius: '5px',
            minWidth: '103px',
            height: '40px',
            bgcolor: '#98A6AD',
            color: '#fff',
            '&:hover': { background: '#ddd' },
            fontSize: '13px',
            fontWeight: 700,
          }}>
          {t('Cancel')}
        </Button>
        <Button
          id="store-create-button"
          onClick={handleSubmit(submit)}
          // disabled={user.userPoint}
          variant="contained"
          color="primary"
          sx={{ borderRadius: '5px', minWidth: '183px', height: '40px', ml: 1.25 }}>
          {t('Save')}
        </Button>
      </Box>
    </Box>
  );
};

export default StoreCreate;
